import { useCallback, useEffect, useRef, useState } from 'react';

import { Form, Modal, notification } from 'antd';
import moment from 'moment';
import { useIntl } from 'react-intl';

import type { TotemWithCreator } from '@src/models/interfaces/totem.interface';
import { ALLOWED_ROLES, asAllowedRoles } from '@src/packages/utils/access';
import {
  TOTEM_EXPIRATION_HOURS_OPTIONS,
  TOTEM_NAME_MAX_LENGTH,
  TOTEM_NO_EXPIRATION_VALUE,
} from '@src/packages/workJourney/constants';
import { DateUtil } from '@src/packages/utils/dates';
import TotemService from '@src/services/TotemService';

export type MarkingZoneDrawerStep = 'list' | 'form';

export interface MarkingZoneFormValues {
  name: string;
  startAt: moment.Moment;
  expiresIn: number | null;
}

interface UseMarkingZoneDrawerParams {
  organizationId: string;
}

export const useMarkingZoneDrawer = ({ organizationId }: UseMarkingZoneDrawerParams) => {
  const intl = useIntl();
  const [form] = Form.useForm<MarkingZoneFormValues>();
  const totemServiceRef = useRef<TotemService | null>(null);

  if (totemServiceRef.current === null) {
    totemServiceRef.current = new TotemService();
  }

  const totemService = totemServiceRef.current;

  const [isOpen, setIsOpen] = useState(false);
  const [step, setStep] = useState<MarkingZoneDrawerStep>('list');
  const [totems, setTotems] = useState<TotemWithCreator[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [revokingJti, setRevokingJti] = useState<string | null>(null);

  const loadTotems = useCallback(async () => {
    if (!organizationId) {
      setTotems([]);
      setStep('list');
      return;
    }

    setLoading(true);

    try {
      const result = await totemService.listTotemsWithCreators(organizationId);
      setTotems(result);
      setStep('list');
    } catch (error) {
      const message = error instanceof Error ? error.message : String(error);
      notification.error({
        message: intl.formatMessage({ id: 'workJourney.markingZone.loadError' }),
        description: message,
      });
      setTotems([]);
      setStep('list');
    } finally {
      setLoading(false);
    }
  }, [intl, organizationId, totemService]);

  const openDrawer = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsOpen(false);
    setStep('list');
    setTotems([]);
    form.resetFields();
  }, [form]);

  const handleActivateClick = useCallback(() => {
    if (step === 'list') {
      form.setFieldsValue({
        name: '',
        startAt: moment(),
        expiresIn: TOTEM_NO_EXPIRATION_VALUE,
      });
      setStep('form');
      return;
    }

    if (step === 'form') {
      form.submit();
    }
  }, [form, step]);

  const handleCancelForm = useCallback(() => {
    form.resetFields();
    setStep('list');
  }, [form]);

  const handleFormFinish = useCallback(
    async (values: MarkingZoneFormValues) => {
      if (!organizationId) {
        return;
      }

      setSubmitting(true);

      try {
        const updated = await totemService.createTotem(
          organizationId,
          values.name,
          values.startAt,
          values.expiresIn,
        );

        setTotems(updated);
        setStep('list');
        form.resetFields();
        notification.success({
          message: intl.formatMessage({ id: 'workJourney.markingZone.createSuccess' }),
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        notification.error({
          message: intl.formatMessage({ id: 'workJourney.markingZone.createError' }),
          description: message,
        });
      } finally {
        setSubmitting(false);
      }
    },
    [form, intl, organizationId, totemService],
  );

  const formatActivationDate = useCallback((date: Date | null): string => {
    if (!date) {
      return '-';
    }

    return DateUtil.toDateString(
      date,
      `${DateUtil.DEFAULT_DT_DATE_FORMAT} ${DateUtil.DEFAULT_TIME_FORMAT}`,
    );
  }, []);

  const formatExpiration = useCallback(
    (hours: number | null): string => {
      if (!hours) {
        return intl.formatMessage({ id: 'workJourney.markingZone.noExpiration' });
      }

      return intl.formatMessage(
        { id: 'workJourney.markingZone.expirationHours' },
        { hours },
      );
    },
    [intl],
  );

  const resolveTotemDisplayName = useCallback(
    (totem: TotemWithCreator): string => totem.name,
    [],
  );

  const expirationOptions = [
    {
      value: TOTEM_NO_EXPIRATION_VALUE,
      label: '',
    },
    ...TOTEM_EXPIRATION_HOURS_OPTIONS.map(hours => ({
      value: hours,
      label: intl.formatMessage({ id: 'workJourney.markingZone.expirationOption' }, { hours }),
    })),
  ];

  const handleGoToPortal = useCallback((totem: TotemWithCreator) => {
    window.open(totem.url, '_blank', 'noopener,noreferrer');
  }, []);

  const handleRevokeTotem = useCallback(
    async (totem: TotemWithCreator) => {
      if (!organizationId) {
        return;
      }

      setRevokingJti(totem.jti);

      try {
        await totemService.revokeTotem(organizationId, totem.jti);
        setTotems(prev => prev.filter(item => item.jti !== totem.jti));
        notification.success({
          message: intl.formatMessage({ id: 'workJourney.markingZone.deleteSuccess' }),
        });
      } catch (error) {
        const message = error instanceof Error ? error.message : String(error);
        notification.error({
          message: intl.formatMessage({ id: 'workJourney.markingZone.deleteError' }),
          description: message,
        });
        throw error;
      } finally {
        setRevokingJti(null);
      }
    },
    [intl, organizationId, totemService],
  );

  const handleDeactivateClick = useCallback(
    (totem: TotemWithCreator) => {
      Modal.confirm({
        centered: true,
        icon: null,
        title: intl.formatMessage({ id: 'workJourney.markingZone.deleteModal.title' }),
        content: intl.formatMessage(
          { id: 'workJourney.markingZone.deleteModal.description' },
          { name: resolveTotemDisplayName(totem) },
        ),
        cancelText: intl.formatMessage({ id: 'button.cancel' }),
        okText: intl.formatMessage({ id: 'workJourney.markingZone.delete' }),
        onOk: () => handleRevokeTotem(totem),
      });
    },
    [handleRevokeTotem, intl, resolveTotemDisplayName],
  );

  const drawerActionButtons = (() => {
    const createAllowedRole = asAllowedRoles(ALLOWED_ROLES.ORGANIZATIONS.WORK_JOURNEY.CREATE);

    if (step === 'form') {
      return [
        {
          iconName: 'close',
          type: 'default' as const,
          label: intl.formatMessage({ id: 'button.cancel' }),
          action: () => handleCancelForm(),
          disabled: submitting,
          allowedRole: createAllowedRole,
        },
        {
          iconName: 'check',
          type: 'primary' as const,
          label: intl.formatMessage({ id: 'workJourney.markingZone.create' }),
          action: () => handleActivateClick(),
          disabled: loading || submitting,
          loading: submitting,
          allowedRole: createAllowedRole,
        },
      ];
    }

    return [
      {
        iconName: 'add',
        type: 'primary' as const,
        label: intl.formatMessage({ id: 'workJourney.markingZone.create' }),
        action: () => handleActivateClick(),
        disabled: loading || submitting,
        allowedRole: createAllowedRole,
      },
    ];
  })();

  useEffect(() => {
    if (isOpen) {
      void loadTotems();
    }
  }, [isOpen, loadTotems]);

  return {
    isOpen,
    step,
    totems,
    loading,
    submitting,
    revokingJti,
    form,
    nameMaxLength: TOTEM_NAME_MAX_LENGTH,
    openDrawer,
    closeDrawer,
    handleFormFinish,
    handleGoToPortal,
    handleDeactivateClick,
    drawerActionButtons,
    formatActivationDate,
    formatExpiration,
    resolveTotemDisplayName,
    expirationOptions,
  };
};

export type UseMarkingZoneDrawerReturn = ReturnType<typeof useMarkingZoneDrawer>;
